import { normalizeValidationId } from '../validationConstants';
import type { ValidationIssue } from '../types';
import {
  isKnownObjectType,
  isSkeyRequiredForObjectType,
} from '../../../shared/config/pcfEtalonCatalog';

export type ValidationRepairSurface =
  | 'mapping'
  | 'quick-fill'
  | 'model'
  | 'validation'
  | 'process-properties'
  | 'bolt-connection';

export type ValidationRepairRoutingReason =
  | 'mapping-missing-model-field'
  | 'mapping-value-coverage'
  | 'mapping-incomplete'
  | 'required-export-missing'
  | 'format-review'
  | 'end-type-review'
  | 'parameter-review'
  | 'connector-breaks'
  | 'connector-angle'
  | 'orphaned-elements'
  | 'connector-size'
  | 'system-assignment'
  | 'geometry-overlap'
  | 'system-name'
  | 'system-name-collision'
  | 'pipe-spec-connection'
  | 'pipe-spec-system'
  | 'bolt-parameters'
  | 'object-type-category-match'
  | 'component-classification'
  | 'optional-skey'
  | 'manual-review';

export interface ValidationRepairRoutingPolicyResult {
  normalizedValidationId: number;
  upperMessage: string;
  surface: ValidationRepairSurface;
  reason: ValidationRepairRoutingReason;
}

function hasAnySignal(text: string, patterns: string[]): boolean {
  return patterns.some((pattern) => text.includes(pattern));
}

function getIssueParameterName(issue: ValidationIssue): string {
  return (
    issue.AdditionalData?.parameterName ||
    issue.AdditionalData?.skeyParameterName ||
    ''
  ).trim().toUpperCase();
}

function extractIssueObjectType(issue: ValidationIssue): string {
  const explicitObjectType = (
    issue.AdditionalData?.objectType ||
    issue.AdditionalData?.objectTypeValue ||
    issue.AdditionalData?.currentObjectType ||
    ''
  ).trim();

  if (explicitObjectType) {
    return explicitObjectType;
  }

  return issue.Message.match(/OBJECT-TYPE\s+['"]([^'"]+)['"]/i)?.[1]?.trim() ?? '';
}

function isMissingSkeyIssue(issue: ValidationIssue, normalizedValidationId: number, upperMessage: string): boolean {
  if (!upperMessage.includes('SKEY')) {
    return false;
  }

  if (upperMessage.includes('MISSING SKEY')) {
    return true;
  }

  return normalizedValidationId === 2 &&
    getIssueParameterName(issue) === 'SKEY' &&
    hasAnySignal(upperMessage, ['MISSING', 'NOT DEFINED', 'NO VALUE', 'NO VALUES', 'IS EMPTY', 'EMPTY']);
}

function hasOptionalEtalonSkey(issue: ValidationIssue, normalizedValidationId: number, upperMessage: string): boolean {
  if (!isMissingSkeyIssue(issue, normalizedValidationId, upperMessage)) {
    return false;
  }

  const objectType = extractIssueObjectType(issue);
  return isKnownObjectType(objectType) && !isSkeyRequiredForObjectType(objectType);
}

export function resolveValidationRepairRoutingPolicy(
  issue: ValidationIssue
): ValidationRepairRoutingPolicyResult {
  const upperMessage = (issue.Message || '').toUpperCase();
  const rawValidationId = issue.ValidationId;
  const isMergedSystemNameValidation = rawValidationId === 32 || issue.AdditionalData?.mergedFromValidationId === '32';
  const normalizedValidationId = normalizeValidationId(issue.ValidationId);

  if (
    normalizedValidationId === 34 &&
    hasAnySignal(upperMessage, ['MAPPED PARAMETER', 'TARGET REVIT PARAMETER']) &&
    hasAnySignal(upperMessage, ['NOT FOUND', 'MISSING IN THE MODEL', 'MISSING FROM THE MODEL'])
  ) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'mapping',
      reason: 'mapping-missing-model-field',
    };
  }

  if (
    normalizedValidationId === 34 &&
    (
      upperMessage.includes('HAS NO VALUES IN CURRENT MODEL SCOPE') ||
      (upperMessage.includes('BOM FIELD') && upperMessage.includes('IS EMPTY'))
    )
  ) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'quick-fill',
      reason: 'mapping-value-coverage',
    };
  }

  if (normalizedValidationId === 34 && upperMessage.includes('REQUIRED MAPPING')) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'mapping',
      reason: 'mapping-incomplete',
    };
  }

  if ([11, 33].includes(normalizedValidationId)) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'quick-fill',
      reason: 'end-type-review',
    };
  }

  if ([13, 17, 18, 19].includes(normalizedValidationId)) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'quick-fill',
      reason: 'parameter-review',
    };
  }

  if (hasOptionalEtalonSkey(issue, normalizedValidationId, upperMessage)) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'validation',
      reason: 'optional-skey',
    };
  }

  if (
    normalizedValidationId === 15 ||
    normalizedValidationId === 35 ||
    upperMessage.includes('DOES NOT MATCH STANDARD FORMAT') ||
    upperMessage.includes('IS TOO SHORT')
  ) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'quick-fill',
      reason: 'format-review',
    };
  }

  if (
    normalizedValidationId === 2 ||
    hasAnySignal(upperMessage, ['MISSING OBJECT-TYPE', 'MISSING SKEY', 'MISSING PIPING-SPEC', 'MISSING MATERIAL'])
  ) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'quick-fill',
      reason: 'required-export-missing',
    };
  }

  if (
    [1, 21, 22].includes(normalizedValidationId) &&
    (upperMessage.includes('SYSTEM BREAK') || upperMessage.includes('DISCONNECTED'))
  ) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'model',
      reason: 'connector-breaks',
    };
  }

  if (
    [1, 21, 22].includes(normalizedValidationId) &&
    upperMessage.includes('ANGLE MISMATCH')
  ) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'model',
      reason: 'connector-angle',
    };
  }

  if (normalizedValidationId === 5) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'model',
      reason: 'orphaned-elements',
    };
  }

  if (
    normalizedValidationId === 21 ||
    upperMessage.includes('SIZE MISMATCH') ||
    upperMessage.includes('DIAMETER MISMATCH')
  ) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'model',
      reason: 'connector-size',
    };
  }

  if (
    isMergedSystemNameValidation &&
    upperMessage.includes('COLLAPSE TO THE SAME EXPORT FILE KEY')
  ) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'validation',
      reason: 'system-name-collision',
    };
  }

  if (
    isMergedSystemNameValidation &&
    (
      upperMessage.includes('CONTAINS CHARACTERS INVALID FOR FILE NAMES') ||
      upperMessage.includes('FILE-SAFE')
    )
  ) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'model',
      reason: 'system-name',
    };
  }

  if (
    isMergedSystemNameValidation &&
    (
      upperMessage.includes('NO SYSTEM NAME') ||
      upperMessage.includes('CANNOT BE GROUPED FOR EXPORT ALL')
    )
  ) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'model',
      reason: 'system-assignment',
    };
  }

  if (normalizedValidationId === 8 || normalizedValidationId === 9) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'model',
      reason: 'system-assignment',
    };
  }

  if (normalizedValidationId === 28 || upperMessage.includes('GEOMETRIC OVERLAP')) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'model',
      reason: 'geometry-overlap',
    };
  }

  if (
    normalizedValidationId === 26 &&
    (
      upperMessage.includes('CONNECTED ELEMENTS HAVE DIFFERENT PIPE-SPEC') ||
      issue.ElementIds.length > 0
    )
  ) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'quick-fill',
      reason: 'pipe-spec-connection',
    };
  }

  if (normalizedValidationId === 20) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'bolt-connection',
      reason: 'bolt-parameters',
    };
  }

  if (normalizedValidationId === 26) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'process-properties',
      reason: 'pipe-spec-system',
    };
  }

  if (normalizedValidationId === 4) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'quick-fill',
      reason: 'object-type-category-match',
    };
  }

  if ([3, 7].includes(normalizedValidationId) || upperMessage.includes('UNSUPPORTED OBJECT-TYPE')) {
    return {
      normalizedValidationId,
      upperMessage,
      surface: 'quick-fill',
      reason: 'component-classification',
    };
  }

  return {
    normalizedValidationId,
    upperMessage,
    surface: 'validation',
    reason: 'manual-review',
  };
}
